import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MicrophoneFilledIcon } from "@/components/Icons";
import {
  getGlobalDictationStatusAsync,
  stopGlobalDictationFlowSessionAsync,
} from "@/models/useGlobalDictation";
import { getPlatform, getPlatformRuntime } from "@/platform";
import { logClientEvent } from "@/telemetry";
import { stopDictationFlowSession } from "@/dictation-interactions";

// Shown when the keyboard's mic key hands off to the app via
// onetranscribe://flow-session/start. The native side has already armed the
// recorder by the time the webview sees the URL; without this overlay the
// user lands on a random app page with zero acknowledgment that anything
// happened. Wispr Flow treats this hand-off as a first-class screen
// ("Flow will now take you back to your previous app") — this is our
// equivalent: confirm the mic is live, teach the swipe-back, show how long
// the window lasts, and offer an off switch.
const FLOW_SESSION_URL_PREFIX = "onetranscribe://flow-session";

// The keyboard tells the app to arm the recorder slightly before the plugin
// can observe it; small grace before reading status.
const STATUS_READ_DELAY_MS = 250;

const formatRemaining = (ms: number) => {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

const AlertFlowSessionArmed = () => {
  const { t } = useTranslation();
  const [expiresAt, setExpiresAt] = useState<number>(0);
  const [now, setNow] = useState<number>(() => Date.now());
  const visibleRef = useRef(false);

  const visible = expiresAt > now;
  visibleRef.current = visible;

  const dismiss = useCallback(() => setExpiresAt(0), []);

  const showFromStatus = useCallback(async () => {
    try {
      const status = await getGlobalDictationStatusAsync();
      if (status?.flowSessionActive && status.flowSessionExpiresAt) {
        setNow(Date.now());
        setExpiresAt(status.flowSessionExpiresAt);
        logClientEvent("dictation.flowSessionShown");
      }
    } catch {
      // Status read failed — the session may still be live; the keyboard
      // remains the source of truth, just skip the overlay.
    }
  }, []);

  // The keyboard's hand-off arrives as appUrlOpen when the app was alive,
  // or as the launch URL on a cold start.
  useEffect(() => {
    if (getPlatform() !== "ios") return;

    let urlListener: { remove: () => Promise<void> } | undefined;
    const handleUrl = (url: string | undefined) => {
      if (!url || !url.startsWith(FLOW_SESSION_URL_PREFIX)) return;
      setTimeout(showFromStatus, STATUS_READ_DELAY_MS);
    };

    const app = getPlatformRuntime().app;
    app.onUrlOpen((event) =>
      handleUrl(event?.url)
    ).then((listener) => {
      urlListener = listener;
    });
    app.getLaunchUrl()
      .then(handleUrl)
      .catch(() => {});

    return () => {
      urlListener?.remove();
    };
  }, [showFromStatus]);

  // Live countdown + auto-hide when the window closes. Also re-check on
  // resume: if the user comes back much later the session may already be
  // dead (or extended by dictating — the expiry stamp is refreshed on use).
  useEffect(() => {
    if (!visible) return;

    const interval = setInterval(() => setNow(Date.now()), 1000);
    let resumeListener: { remove: () => Promise<void> } | undefined;
    getPlatformRuntime().app.onResume(() => {
      getGlobalDictationStatusAsync()
        .then((status) => {
          if (!visibleRef.current) return;
          if (status?.flowSessionActive && status.flowSessionExpiresAt) {
            setExpiresAt(status.flowSessionExpiresAt);
          } else {
            dismiss();
          }
        })
        .catch(() => {});
    }).then((listener) => {
      resumeListener = listener;
    });

    return () => {
      clearInterval(interval);
      resumeListener?.remove();
    };
  }, [visible, dismiss]);

  if (!visible) return null;

  const turnOff = () => {
    logClientEvent(
      "dictation.flowSessionDisabled"
    );
    stopDictationFlowSession(stopGlobalDictationFlowSessionAsync, dismiss);
  };

  return (
    <div
      className="app-page-background fixed inset-0 z-[60] flex flex-col bg-[var(--bg-primary)] text-[var(--text-primary)]"
      style={{
        paddingTop: "calc(env(safe-area-inset-top) + 1.5rem)",
        paddingBottom: "calc(env(safe-area-inset-bottom) + 1.5rem)",
      }}
    >
      <div className="flex flex-1 flex-col items-center justify-center px-6 text-center">
        <span className="relative flex h-24 w-24 items-center justify-center">
          <span className="absolute inset-0 animate-ping rounded-full bg-[var(--accent)] opacity-20" />
          <span className="relative flex h-20 w-20 items-center justify-center rounded-full bg-[var(--accent)] text-[var(--text-inverse)] shadow-2xl shadow-[var(--accent-glow)]">
            <MicrophoneFilledIcon size={36} />
          </span>
        </span>
        <h1 className="mt-6 text-3xl font-semibold leading-tight">
          {t("globalDictationFlowArmed.title", {
            defaultValue: "Your mic is on",
          })}
        </h1>
        <p className="mt-3 max-w-[300px] text-base leading-relaxed text-[var(--text-secondary)]">
          {t("globalDictationFlowArmed.body", {
            defaultValue:
              "Swipe back to the app you were typing in and tap the mic on the 1Transcribe keyboard.",
          })}
        </p>
        <span className="mt-6 rounded-full glass-tile px-4 py-2 text-sm font-medium tabular-nums text-[var(--text-secondary)]">
          {t("globalDictationFlowArmed.countdown", {
            defaultValue: "Mic stays ready · {{remaining}}",
            remaining: formatRemaining(expiresAt - now),
          })}
        </span>
      </div>
      <div className="space-y-3 px-6">
        <button
          data-action-id="dictation.flowSession.dismiss"
          onClick={dismiss}
          className="h-14 w-full rounded-full bg-[var(--accent)] text-base font-semibold text-[var(--text-inverse)] active:scale-[0.99] transition-transform"
        >
          {t("globalDictationFlowArmed.gotIt", {
            defaultValue: "Got it — taking me back",
          })}
        </button>
        <button
          data-action-id="dictation.flowSession.stop"
          onClick={turnOff}
          className="h-12 w-full rounded-full glass-tile text-sm font-semibold text-[var(--text-primary)] active:bg-[var(--glass-bg-active)]"
        >
          {t("globalDictationFlowArmed.turnOff", {
            defaultValue: "Turn the mic off",
          })}
        </button>
      </div>
    </div>
  );
};

export default AlertFlowSessionArmed;
